import { useNavigate } from 'react-router-dom'
import { Download, Eye } from 'lucide-react'
import type { SessionWithId } from '../../lib/organization'
import { getSessionArtifacts, getSessionMessages } from '../../lib/organization'
import { STAGE_NAMES } from '../../types'
import type { Stage } from '../../types'
import { downloadSessionBundle } from '../../lib/download'
import { useState } from 'react'

interface OrgSessionCardProps {
  session: SessionWithId
  orgId: string
}

const STATUS_STYLES: Record<string, { label: string; className: string }> = {
  completed: { label: 'Done', className: 'border-teal text-teal' },
  active: { label: 'Active', className: 'border-amber text-amber-dark' },
  lobby: { label: 'In lobby', className: 'border-navy/55 text-navy/70' },
}

export default function OrgSessionCard({ session, orgId }: OrgSessionCardProps) {
  const navigate = useNavigate()
  const [downloading, setDownloading] = useState(false)
  const status = STATUS_STYLES[session.status] || STATUS_STYLES.lobby
  const date = session.createdAt?.toDate?.()
  const dateStr = date ? date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : ''

  async function handleDownload() {
    setDownloading(true)
    try {
      const [messages, artifacts] = await Promise.all([
        getSessionMessages(session.id),
        getSessionArtifacts(session.id),
      ])
      await downloadSessionBundle(session.sessionName, messages, artifacts, [], [])
    } finally {
      setDownloading(false)
    }
  }

  return (
    <div className="card card-shadow card-lift p-5">
      <div className="flex items-start justify-between mb-3">
        <h3 className="font-display text-base text-navy truncate flex-1 mr-2">{session.sessionName}</h3>
        <span className={`mono-label border px-1.5 py-0.5 shrink-0 ${status.className}`}>
          {status.label}
        </span>
      </div>

      <div className="text-xs text-navy/70 space-y-1 mb-4">
        <p>{dateStr}</p>
        <p>Stage {session.currentStage}/5: {STAGE_NAMES[session.currentStage as Stage]}</p>
        <p>{session.teamSize} participant{session.teamSize !== 1 ? 's' : ''}</p>
      </div>

      <div className="flex gap-2">
        {session.status === 'completed' ? (
          <>
            <button
              onClick={handleDownload}
              disabled={downloading}
              className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 mono-label text-navy border-2 border-navy hover:bg-paper transition-colors disabled:opacity-50 cursor-pointer"
            >
              <Download className="w-3.5 h-3.5" />
              {downloading ? 'Downloading...' : 'Bundle'}
            </button>
            <button
              onClick={() => navigate(`/org/${orgId}/session/${session.id}`)}
              className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 mono-label text-amber-dark border-2 border-amber hover:bg-paper transition-colors cursor-pointer"
            >
              <Eye className="w-3.5 h-3.5" />
              View
            </button>
          </>
        ) : (
          <button
            onClick={() => navigate(`/lobby/${session.id}`)}
            className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 mono-label text-white bg-amber border-2 border-navy hover:bg-amber-dark transition-colors cursor-pointer"
          >
            Join
          </button>
        )}
      </div>
    </div>
  )
}
